import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Instagram, Facebook, ChevronDown } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [openSection, setOpenSection] = useState(null);

  const destinations = [
    { name: 'Tour de Corse', path: '/tour-de-corse-croisiere-catamaran' },
    { name: 'Corse du Sud', path: '/croisiere-catamaran-corse-du-sud' },
    { name: 'Ouest Corse', path: '/croisiere-catamaran-ouest-corse' },
    { name: 'Sardaigne & Corse', path: '/croisiere-catamaran-sardaigne-corse' },
    { name: 'Grèce authentique', path: '/croisiere-catamaran-grece-authentique' },
    { name: 'Les Grenadines', path: '/croisiere-catamaran-grenadines-caraibes' },
  ];

  const quickLinks = [
    { name: 'Slow Tourisme', path: '/slow-tourisme-croisieres-responsables' },
    { name: 'Nos Navires', path: '/les-navires-disponibles' },
    { name: 'Réservation', path: '/reservation-cabines-privatisation-catamaran-sognudimare' },
    { name: 'Club des voyageurs', path: '/club' },
    { name: 'Contact', path: '/contact' },
  ];

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/sognudimare/', label: 'Instagram' },
    { icon: Facebook, href: 'https://www.facebook.com/sognudimare', label: 'Facebook' },
  ];

  const toggleSection = (section) => {
    setOpenSection(prev => prev === section ? null : section);
  };

  // Collapsible section for mobile
  const MobileAccordion = ({ title, id, children }) => (
    <div className="md:hidden border-b border-white/10">
      <button
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between py-4 text-left"
        data-testid={`footer-accordion-${id}`}
      >
        <h4 className="font-display text-base font-semibold">{title}</h4>
        <ChevronDown className={`h-4 w-4 text-white/50 transition-transform ${openSection === id ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${openSection === id ? 'max-h-96 pb-4' : 'max-h-0'}`}>
        {children}
      </div>
    </div>
  );

  return (
    <footer className="bg-[#0A2540] text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        {/* Brand - always visible */}
        <div className="text-center md:text-left mb-8 md:mb-0">
          <div className="md:hidden">
            <Link to="/" className="inline-flex items-center gap-2 mb-4" data-testid="footer-logo-mobile">
              <img 
                src="https://static.wixstatic.com/media/ce6ce7_a82e3e86741143d6ab7acd99c121af7b~mv2.png/v1/fill/w_600,h_300,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/croisieres%20catamaran%20corse%20sognudimare.png"
                alt="SOGNUDIMARE"
                className="h-10 object-contain"
                style={{ filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.15))' }}
              />
              <span 
                className="text-lg"
                style={{ fontFamily: "'Comfortaa', cursive", fontWeight: 700, letterSpacing: '0.02em' }}
              >
                <span className="text-white">sognu</span>
                <span className="text-[#2A9D8F]">di</span>
                <span className="text-white">mare</span>
              </span>
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-5 max-w-sm mx-auto">
              Croisières authentiques en catamaran. Slow tourisme en pension complète avec équipage.
            </p>
          </div>
        </div>

        {/* Mobile Accordion sections */}
        <div className="md:hidden px-0">
          <MobileAccordion title="Destinations" id="destinations">
            <ul className="space-y-3 pl-1">
              {destinations.map((dest) => (
                <li key={dest.path}>
                  <Link to={dest.path} className="text-white/60 hover:text-[#2A9D8F] transition-colors text-sm block py-0.5">
                    {dest.name}
                  </Link>
                </li>
              ))}
            </ul>
          </MobileAccordion>
          <MobileAccordion title="Liens rapides" id="links">
            <ul className="space-y-3 pl-1">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-white/60 hover:text-[#2A9D8F] transition-colors text-sm block py-0.5">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </MobileAccordion>
          <MobileAccordion title="Contact" id="contact">
            <ul className="space-y-4 pl-1">
              <li>
                <a href="tel:0495729028" className="flex items-center gap-3 text-white/60 hover:text-[#2A9D8F]">
                  <Phone className="h-5 w-5 text-[#2A9D8F]" />
                  <span className="text-sm">04 95 72 90 28</span>
                </a>
              </li>
              <li>
                <a href="mailto:contact@sognudimare-catamarans.com" className="flex items-center gap-3 text-white/60 hover:text-[#2A9D8F]">
                  <Mail className="h-5 w-5 text-[#2A9D8F] shrink-0" />
                  <span className="text-sm break-all">contact@sognudimare-catamarans.com</span>
                </a>
              </li>
            </ul>
          </MobileAccordion>
        </div>

        {/* Desktop Grid - hidden on mobile */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-2 mb-6" data-testid="footer-logo">
              <img 
                src="https://static.wixstatic.com/media/ce6ce7_a82e3e86741143d6ab7acd99c121af7b~mv2.png/v1/fill/w_600,h_300,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/croisieres%20catamaran%20corse%20sognudimare.png"
                alt="SOGNUDIMARE"
                className="h-12 object-contain"
                style={{ filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.15))' }}
              />
              <span 
                className="text-lg"
                style={{ fontFamily: "'Comfortaa', cursive", fontWeight: 700, letterSpacing: '0.02em' }}
              >
                <span className="text-white">sognu</span>
                <span className="text-[#2A9D8F]">di</span>
                <span className="text-white">mare</span>
              </span>
            </Link>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              Croisières authentiques en catamaran en Corse, Sardaigne, Grèce & Caraïbes. 
              Slow tourisme en pension complète avec équipage.
            </p>
            {/* Réseaux sociaux */}
            <div className="flex items-center gap-2.5" data-testid="footer-socials">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    title={social.label}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#2A9D8F] text-white hover:text-white border border-white/15 hover:border-[#2A9D8F] transition-all hover:-translate-y-0.5"
                    data-testid={`footer-social-${social.label.toLowerCase()}`}
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Destinations */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-6">Destinations</h4>
            <ul className="space-y-3">
              {destinations.map((dest) => (
                <li key={dest.path}>
                  <Link to={dest.path} className="text-white/70 hover:text-[#2A9D8F] transition-colors text-sm">{dest.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links + App Store */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-6">Liens rapides</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-white/70 hover:text-[#2A9D8F] transition-colors text-sm">{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-6">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a href="tel:0495729028" className="flex items-center gap-3 text-white/70 hover:text-[#2A9D8F] transition-colors" data-testid="footer-phone">
                  <Phone className="h-5 w-5 text-[#2A9D8F]" />
                  <span className="text-sm">04 95 72 90 28</span>
                </a>
              </li>
              <li>
                <a href="mailto:contact@sognudimare-catamarans.com" className="flex items-center gap-3 text-white/70 hover:text-[#2A9D8F] transition-colors" data-testid="footer-email">
                  <Mail className="h-5 w-5 text-[#2A9D8F] shrink-0" />
                  <span className="text-sm break-all">contact@sognudimare-catamarans.com</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* SEO Internal Link Mesh — drives indexation + topical authority */}
      <div className="border-t border-white/10 bg-[#061a30]" data-testid="seo-footer-mesh">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-xs">
            <div>
              <div className="text-[#D4AF37] font-bold uppercase tracking-widest mb-3 text-[10px]">Croisières Catamaran Corse</div>
              <ul className="space-y-1.5 text-white/55">
                <li><Link to="/tour-de-corse-croisiere-catamaran" className="hover:text-white">Tour de Corse en catamaran</Link></li>
                <li><Link to="/croisiere-catamaran-corse-du-sud" className="hover:text-white">Croisière catamaran Corse du Sud</Link></li>
                <li><Link to="/croisiere-catamaran-ouest-corse" className="hover:text-white">Croisière catamaran Ouest Corse</Link></li>
                <li><Link to="/croisiere-catamaran-sardaigne-corse" className="hover:text-white">Croisière catamaran Sardaigne</Link></li>
                <li><Link to="/croisiere-catamaran-grece-authentique" className="hover:text-white">Croisière catamaran Grèce</Link></li>
                <li><Link to="/croisiere-catamaran-grece-cyclades-2-semaines" className="hover:text-white">Croisière catamaran Cyclades</Link></li>
                <li><Link to="/croisiere-catamaran-grenadines-caraibes" className="hover:text-white">Croisière catamaran Grenadines</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-[#D4AF37] font-bold uppercase tracking-widest mb-3 text-[10px]">Type de Croisière</div>
              <ul className="space-y-1.5 text-white/55">
                <li><Link to="/reservation-cabines-privatisation-catamaran-sognudimare" className="hover:text-white">Croisière en cabine à 2</Link></li>
                <li><Link to="/reservation-cabines-privatisation-catamaran-sognudimare" className="hover:text-white">Privatisation catamaran famille</Link></li>
                <li><Link to="/reservation-cabines-privatisation-catamaran-sognudimare" className="hover:text-white">Catamaran couple</Link></li>
                <li><Link to="/reservation-cabines-privatisation-catamaran-sognudimare" className="hover:text-white">Catamaran luxe avec équipage</Link></li>
                <li><Link to="/reservation-cabines-privatisation-catamaran-sognudimare" className="hover:text-white">Croisière pension complète</Link></li>
                <li><Link to="/reservation-cabines-privatisation-catamaran-sognudimare" className="hover:text-white">Croisière tout inclus catamaran</Link></li>
                <li><Link to="/slow-tourisme-croisieres-responsables" className="hover:text-white">Croisière éco-responsable</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-[#D4AF37] font-bold uppercase tracking-widest mb-3 text-[10px]">Ports d'embarquement</div>
              <ul className="space-y-1.5 text-white/55">
                <li><Link to="/contact" className="hover:text-white">Croisière catamaran Ajaccio</Link></li>
                <li><Link to="/contact" className="hover:text-white">Croisière catamaran Porto-Vecchio</Link></li>
                <li><Link to="/contact" className="hover:text-white">Croisière catamaran Calvi</Link></li>
                <li><Link to="/contact" className="hover:text-white">Catamaran Bonifacio</Link></li>
                <li><Link to="/contact" className="hover:text-white">Catamaran Scandola Piana</Link></li>
                <li><Link to="/contact" className="hover:text-white">Catamaran Lavezzi Maddalena</Link></li>
                <li><Link to="/contact" className="hover:text-white">Catamaran Méditerranée</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-[#D4AF37] font-bold uppercase tracking-widest mb-3 text-[10px]">Notre flotte</div>
              <ul className="space-y-1.5 text-white/55">
                <li><Link to="/lagoon-46-sognudimare" className="hover:text-white">Catamaran LAGOON 46</Link></li>
                <li><Link to="/lagoon-43-sognudimare" className="hover:text-white">Catamaran LAGOON 43</Link></li>
                <li><Link to="/lagoon-38-sognudimare" className="hover:text-white">Catamaran LAGOON 38</Link></li>
                <li><Link to="/les-navires-disponibles" className="hover:text-white">Tous nos catamarans</Link></li>
                <li><Link to="/carte-club-des-voyageurs" className="hover:text-white">Club fidélité SOGNUDIMARE</Link></li>
                <li><Link to="/cgv" className="hover:text-white">Engagement Climat 12 mois</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Paiement sécurisé */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-2">
              <svg className="h-4 w-4 text-[#2A9D8F]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <span className="text-white font-semibold text-xs sm:text-sm tracking-wide" data-testid="secure-payment-label">PAIEMENT SECURISE SQUARE</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-white/10 rounded px-1.5 py-0.5"><svg viewBox="0 0 48 32" className="h-5 w-auto" fill="none"><rect width="48" height="32" rx="4" fill="#1A1F71"/><text x="24" y="20" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold" fontFamily="Arial, sans-serif">CB</text></svg></div>
              <div className="bg-white/10 rounded px-1.5 py-0.5"><svg viewBox="0 0 48 32" className="h-5 w-auto" fill="none"><rect width="48" height="32" rx="4" fill="#fff"/><text x="24" y="21" textAnchor="middle" fill="#1A1F71" fontSize="13" fontWeight="bold" fontFamily="Arial, sans-serif" fontStyle="italic">VISA</text></svg></div>
              <div className="bg-white/10 rounded px-1.5 py-0.5"><svg viewBox="0 0 48 32" className="h-5 w-auto"><rect width="48" height="32" rx="4" fill="#252525"/><circle cx="19" cy="16" r="9" fill="#EB001B"/><circle cx="29" cy="16" r="9" fill="#F79E1B"/><path d="M24 9.34a9 9 0 0 1 0 13.32 9 9 0 0 1 0-13.32z" fill="#FF5F00"/></svg></div>
              <div className="bg-white/10 rounded px-1.5 py-0.5"><svg viewBox="0 0 48 32" className="h-5 w-auto"><rect width="48" height="32" rx="4" fill="#006FCF"/><text x="24" y="21" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold" fontFamily="Arial, sans-serif">AMEX</text></svg></div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col items-center gap-3 text-xs sm:text-sm text-white/50">
            <p className="text-center">
              &copy; {currentYear} SOGNUDIMARE. Tous droits réservés. Création{' '}
              <a href="https://www.webisula.com/" target="_blank" rel="noopener noreferrer" className="text-[#2A9D8F] hover:text-white transition-colors" data-testid="webisula-link">
                WEBISULA
              </a>
            </p>
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
              <Link to="/cgv" className="hover:text-white transition-colors" data-testid="footer-cgv-link">CGV</Link>
              <span className="hidden sm:inline">|</span>
              <Link to="/politique-de-confidentialite" className="hover:text-white transition-colors" data-testid="footer-politique-link">Politique de confidentialité</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
